import { useMemo } from 'react';
import {
  Bell,
  BookOpen,
  Brain,
  CheckCircle,
  Circle,
  Database,
  FileText,
  MessageSquare,
  RefreshCw,
  ScanLine,
  Settings,
  TrendingUp,
  Users,
} from 'lucide-react';

import { PageHeader } from '../../../components/shared';
import { cn } from '../../../lib/cn';
import { catColor } from '../../../lib/role-avatar';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useAuthStore } from '../../../stores/auth';
import { useChatbots } from '../../../hooks/useChatbots';
import {
  EmptyHint,
  ErrorHint,
  HBar,
  MetricTile,
  MoreLink,
  StatRow,
  Tile,
  TileHeader,
} from '../components/primitives';
import { useDashboardCounters, useKnowledgeBases } from '../hooks/useDashboard';
import { CanhBaoTokenDashboard } from '../../token-ai/components/CanhBaoTokenDashboard';
import { useDieuHuong } from '../../../hooks/useDuongDan';

/** Một dòng tóm tắt hoạt động; không gắn mốc thời gian vì không có sự kiện thật. */
interface ActivityEntry {
  text: string;
  icon: typeof ScanLine;
  tint: string;
}

export function DashboardView() {
  const navigate = useDieuHuong();
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const user = useAuthStore((s) => s.user);

  const counters = useDashboardCounters();
  const knowledge = useKnowledgeBases();
  const chatbots = useChatbots();

  const loading = counters.isLoading;
  const c = counters.data;

  /**
   * Dòng hoạt động được **tổng hợp ở phía client** từ chính các số đếm đã tải,
   * mỗi dòng chỉ hiện khi số của nó > 0. Đây không phải sự kiện thật nên không
   * hiện mốc thời gian — bản gốc ghi cứng "5 phút trước", sai với dữ liệu thật.
   */
  const activities = useMemo<ActivityEntry[]>(() => {
    if (!c) return [];
    const list: ActivityEntry[] = [];
    if (c.tongChungTu > 0)
      list.push({
        text: t('dashboard.hdOcrHoanTat', { so: soVN(c.tongChungTu, locale) }),
        icon: ScanLine,
        tint: 'text-teal-600',
      });
    if (c.tepTaiLieu > 0)
      list.push({
        text: t('dashboard.hdKhoTriThuc', { so: soVN(c.tepTaiLieu, locale) }),
        icon: Brain,
        tint: 'text-violet-600',
      });
    if (c.chatbotHoatDong > 0)
      list.push({
        text: t('dashboard.hdChatbot', { so: c.chatbotHoatDong }),
        icon: MessageSquare,
        tint: 'text-primary-600',
      });
    if (c.nguoiDungHoatDong > 0)
      list.push({
        text: t('dashboard.hdNguoiDung', {
          dangHoatDong: c.nguoiDungHoatDong,
          tong: c.tongTaiKhoan,
        }),
        icon: Users,
        tint: 'text-orange-600',
      });
    if (c.chungTuDaXacNhan > 0)
      list.push({
        text: t('dashboard.hdDaXacNhan', { so: soVN(c.chungTuDaXacNhan, locale) }),
        icon: CheckCircle,
        tint: 'text-success-600',
      });
    if (c.chungTuDaChuyenKho > 0)
      list.push({
        text: t('dashboard.hdDaChuyenKho', { so: soVN(c.chungTuDaChuyenKho, locale) }),
        icon: Database,
        tint: 'text-sky-600',
      });
    return list;
  }, [c, t, locale]);

  /** Ba chuỗi trạng thái chứng từ; phần trăm tính trên tổng của đúng ba chuỗi đó. */
  const statusSeries = useMemo(() => {
    if (!c) return [];
    const series = [
      { label: t('dashboard.ctNhap'), value: c.chungTuNhap, color: 'bg-warning-500' },
      { label: t('dashboard.ctDaXacNhan'), value: c.chungTuDaXacNhan, color: 'bg-success-500' },
      {
        label: t('dashboard.ctDaChuyenKho'),
        value: c.chungTuDaChuyenKho,
        color: 'bg-violet-500',
      },
    ].filter((s) => s.value > 0);
    // Chặn dưới ở 1 để không chia cho 0.
    const total = Math.max(
      1,
      c.chungTuNhap + c.chungTuDaXacNhan + c.chungTuDaChuyenKho,
    );
    return series.map((s) => ({ ...s, pct: Math.round((s.value / total) * 100), total }));
  }, [c, t]);

  /** Số tệp gom theo phòng ban, giảm dần; thanh so với phòng ban lớn nhất. */
  const byDepartment = useMemo(() => {
    const kbs = knowledge.data ?? [];
    const totals = new Map<string, number>();
    for (const kb of kbs) {
      const dept = kb.managingDepartmentName ?? t('dashboard.phongBanKhac');
      totals.set(dept, (totals.get(dept) ?? 0) + kb.fileCount);
    }
    const rows = [...totals.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    const max = rows[0]?.count ?? 0;
    return { rows, max };
  }, [knowledge.data, t]);

  const today = new Date();
  const subtitle = today.toLocaleDateString(locale, {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const monthChip = today.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const avatarLetter = user?.fullName?.trim()?.[0]?.toUpperCase() ?? 'U';

  /** Lúc tải và lúc lỗi của một ô: không được hiện thành "chưa có dữ liệu". */
  const trangThaiO = (
    q: { isLoading: boolean; error: unknown; refetch: () => unknown },
    /** Số đếm đã có dải lỗi chung ở đầu trang, nên ô con không lặp lại. */
    daCoDaiLoi = false,
  ) => {
    if (q.isLoading) return <EmptyHint>{t('dashboard.dangTai')}</EmptyHint>;
    if (q.error && daCoDaiLoi) return <EmptyHint>—</EmptyHint>;
    if (q.error)
      return (
        <ErrorHint
          message={t('dashboard.loiSoLieu', { loi: dichLoi(q.error) })}
          retryLabel={t('chung.thuLai')}
          onRetry={() => void q.refetch()}
        />
      );
    return null;
  };

  const refetchAll = () => {
    void counters.refetch();
    void knowledge.refetch();
    void chatbots.refetch();
  };

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.tongQuan') }, { label: t('nav.muc.dashboard') }]}
          titleIcon={<TrendingUp size={18} className="text-primary-600" />}
          title={t('dashboard.tieuDe')}
          subtitle={<span className="capitalize">{subtitle}</span>}
          actions={
            <>
              <span className="flex items-center gap-1.5 text-xs font-medium text-success-700">
                <span aria-hidden className="h-2 w-2 animate-pulse rounded-full bg-success-500" />
                {t('dashboard.aiDangHoatDong')}
              </span>

              {/* Phần tử responsive-hidden duy nhất trong cả ứng dụng (§8.1). */}
              <span className="hidden rounded-lg border border-default px-2.5 py-1 text-xs font-medium capitalize text-content-muted md:inline">
                {monthChip}
              </span>

              <button
                type="button"
                onClick={() => navigate('/thong-bao')}
                aria-label={t('nav.muc.thongBao')}
                title={t('nav.muc.thongBao')}
                className="rounded-lg p-1.5 text-content-muted transition-colors hover:bg-subtle hover:text-content-primary"
              >
                <Bell size={16} />
              </button>

              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-default bg-subtle text-xs font-bold text-content-secondary">
                {avatarLetter}
              </span>

              <button
                type="button"
                onClick={refetchAll}
                disabled={loading}
                className="flex items-center gap-1.5 rounded-lg border border-default px-3 py-1.5 text-sm text-content-secondary transition-colors hover:bg-subtle hover:text-content-primary disabled:opacity-50"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                {t('chung.lamMoi')}
              </button>
            </>
          }
        />

        {/*
          Trạng thái lỗi: bản gốc **không xử lý** — hook không phơi nhánh lỗi nên
          khi gọi thất bại màn hình hiện toàn số 0. Đó là vi phạm quy tắc bốn
          trạng thái (§10.1), nên ở đây ta thêm dải lỗi và ghi nhận là sai lệch
          có chủ đích so với bản gốc (§8.1).
        */}
        {counters.error && (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-lg bg-danger-50 px-4 py-3"
          >
            <span className="text-sm text-danger-700">
              {t('dashboard.loiSoLieu', { loi: dichLoi(counters.error) })}
            </span>
            <button
              type="button"
              onClick={() => void counters.refetch()}
              className="shrink-0 rounded-lg border border-default bg-surface px-3 py-1.5 text-xs font-medium text-content-secondary transition-colors hover:bg-subtle"
            >
              {t('chung.thuLai')}
            </button>
          </div>
        )}

        <div className="grid grid-cols-12 gap-4">
          {/* --- Hàng 1: bốn ô chỉ số --- */}
          <MetricTile
            label={t('dashboard.tongChungTu')}
            value={c?.tongChungTu ?? 0}
            sub={t('dashboard.soMauOcr', { so: c?.soMauOcr ?? 0 })}
            icon={<FileText size={20} />}
            tone="primary"
            loading={loading}
            locale={locale}
          />
          <MetricTile
            label={t('dashboard.boTriThuc')}
            value={c?.boTriThuc ?? 0}
            sub={t('dashboard.tepTaiLieu', { so: soVN(c?.tepTaiLieu ?? 0, locale) })}
            icon={<Brain size={20} />}
            tone="violet"
            loading={loading}
            locale={locale}
          />
          <MetricTile
            label={t('dashboard.chatbotHoatDong')}
            value={c?.chatbotHoatDong ?? 0}
            sub={t('dashboard.botDaCauHinh', { so: c?.chatbotDaCauHinh ?? 0 })}
            icon={<MessageSquare size={20} />}
            tone="teal"
            loading={loading}
            locale={locale}
          />
          <MetricTile
            label={t('dashboard.nguoiDungHoatDong')}
            value={c?.nguoiDungHoatDong ?? 0}
            sub={t('dashboard.tongTaiKhoan', { so: c?.tongTaiKhoan ?? 0 })}
            icon={<Users size={20} />}
            tone="success"
            loading={loading}
            locale={locale}
          />

          {/* Chi tiết ở màn Token AI; Dashboard chỉ báo khi cần làm gì đó (từ 80%, đã chặn). */}
          <CanhBaoTokenDashboard />

          {/* --- Hàng 2: hoạt động (5) + biểu đồ trạng thái (4) + xử lý tài liệu (3) --- */}
          <Tile className="col-span-12 lg:col-span-5">
            <TileHeader
              icon={<TrendingUp size={16} className="text-content-muted" />}
              title={t('dashboard.hoatDongGanDay')}
            />
            <div className="px-5 py-2">
              {trangThaiO(counters, true) ? (
                trangThaiO(counters, true)
              ) : activities.length === 0 ? (
                <EmptyHint>{t('dashboard.chuaCoHoatDong')}</EmptyHint>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {activities.map((entry, index) => {
                    const Icon = entry.icon;
                    return (
                      <div key={index} className="flex items-start gap-3 py-3">
                        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-subtle">
                          <Icon size={13} className={entry.tint} />
                        </span>
                        <p className="min-w-0 self-center text-sm leading-snug text-content-primary">
                          {entry.text}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tile>

          <Tile className="col-span-12 sm:col-span-7 lg:col-span-4">
            <TileHeader
              icon={<FileText size={16} className="text-content-secondary" />}
              title={t('dashboard.chungTuTheoTrangThai')}
            />
            <div className="p-5">
              {trangThaiO(counters, true) ? (
                trangThaiO(counters, true)
              ) : statusSeries.length === 0 ? (
                <EmptyHint>{t('dashboard.chuaCoChungTu')}</EmptyHint>
              ) : (
                <div className="space-y-4">
                  {statusSeries.map((s) => (
                    <div key={s.label}>
                      <div className="mb-1.5 flex items-baseline justify-between">
                        <span className="truncate pr-2 text-xs text-content-secondary">
                          {s.label}
                        </span>
                        <span className="text-xs font-bold tabular-nums text-content-primary">
                          {soVN(s.value, locale)}{' '}
                          <span className="font-normal text-content-muted">({s.pct}%)</span>
                        </span>
                      </div>
                      <div className="flex">
                        <HBar value={s.value} max={s.total} color={s.color} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Tile>

          <Tile className="col-span-12 sm:col-span-5 lg:col-span-3">
            <TileHeader
              icon={<ScanLine size={16} className="text-content-muted" />}
              title={t('dashboard.xuLyTaiLieu')}
            />
            <div className="px-5 py-3">
              <StatRow label={t('dashboard.mauOcr')} value={c?.soMauOcr ?? 0} />
              <StatRow
                label={t('dashboard.chungTuNhap')}
                value={soVN(c?.chungTuNhap ?? 0, locale)}
                accent="text-warning-700"
              />
              <StatRow
                label={t('dashboard.daXacNhan')}
                value={soVN(c?.chungTuDaXacNhan ?? 0, locale)}
                accent="text-success-700"
              />
              <StatRow
                label={t('dashboard.daVaoKho')}
                value={soVN(c?.chungTuDaChuyenKho ?? 0, locale)}
                accent="text-violet-700"
              />
              <div className="pt-3">
                <MoreLink onClick={() => navigate('/xu-ly/chung-tu')}>
                  {t('chung.xemTatCa')}
                </MoreLink>
              </div>
            </div>
          </Tile>

          {/* --- Hàng 3: phòng ban (5) + trạng thái bot (4) + cấu hình (3) --- */}
          <Tile className="col-span-12 lg:col-span-5">
            <TileHeader
              icon={<Brain size={16} className="text-content-muted" />}
              title={t('dashboard.triThucTheoPhongBan')}
            />
            <div className="p-5">
              {trangThaiO(knowledge) ? (
                trangThaiO(knowledge)
              ) : byDepartment.rows.length === 0 ? (
                <EmptyHint>{t('dashboard.chuaCoDuLieu')}</EmptyHint>
              ) : (
                <div className="space-y-3.5">
                  {byDepartment.rows.map((row) => (
                    <div key={row.name} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 truncate text-xs text-content-secondary lg:w-28">
                        {row.name}
                      </span>
                      <div className="flex min-w-[64px] flex-1">
                        <HBar value={row.count} max={byDepartment.max} color={catColor(row.name).bar} />
                      </div>
                      <span className="min-w-[2rem] shrink-0 text-right text-xs font-bold tabular-nums text-content-primary">
                        {soVN(row.count, locale)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Tile>

          <Tile className="col-span-12 sm:col-span-7 lg:col-span-4">
            <TileHeader
              icon={<MessageSquare size={16} className="text-content-muted" />}
              title={t('dashboard.trangThaiChatbot')}
              action={
                <MoreLink onClick={() => navigate('/tro-ly-ai/chatbot')}>
                  {t('dashboard.cauHinh')}
                </MoreLink>
              }
            />
            <div className="px-5 py-2">
              {trangThaiO(chatbots) ? (
                trangThaiO(chatbots)
              ) : (chatbots.data ?? []).length === 0 ? (
                <EmptyHint>{t('dashboard.chuaCauHinhChatbot')}</EmptyHint>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {(chatbots.data ?? []).map((bot) => {
                    // Ba trạng thái, không phải hai: bật rồi vẫn có thể không
                    // còn nguồn nào để trả lời. Phải khớp thanh điều hướng, nơi
                    // chỉ liệt kê bot `active && answerable` (§8.1).
                    const state = !bot.active
                      ? {
                          dot: 'bg-content-muted',
                          text: 'text-content-muted',
                          label: t('dashboard.botTat'),
                        }
                      : !bot.answerable
                        ? {
                            dot: 'bg-warning-500',
                            text: 'text-warning-700',
                            label: t('dashboard.botChuaSanSang'),
                          }
                        : {
                            dot: 'bg-success-500',
                            text: 'text-success-700',
                            label: t('dashboard.botHoatDong'),
                          };
                    return (
                      <div key={bot.id} className="flex items-center justify-between gap-2 py-2.5">
                        <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', state.dot)} />
                        <MessageSquare size={14} className="shrink-0 text-content-muted" />
                        <span className="min-w-0 flex-1 truncate text-sm text-content-secondary">
                          {bot.name}
                        </span>
                        <span className={cn('shrink-0 text-xs font-semibold', state.text)}>
                          {state.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tile>

          <Tile className="col-span-12 sm:col-span-5 lg:col-span-3">
            <TileHeader
              icon={<Settings size={16} className="text-content-muted" />}
              title={t('dashboard.cauHinhHeThong')}
            />
            <div className="px-5 py-3">
              <StatRow label={t('dashboard.tongNguoiDung')} value={c?.tongTaiKhoan ?? 0} />
              <StatRow
                label={t('dashboard.dangHoatDong')}
                value={c?.nguoiDungHoatDong ?? 0}
                accent="text-success-700"
              />
              <StatRow
                label={t('dashboard.soVaiTro')}
                value={c?.soVaiTro ?? 0}
                accent="text-primary-700"
              />
              <StatRow
                label={t('dashboard.soPhongBan')}
                value={c?.soPhongBan ?? 0}
                accent="text-teal-700"
              />
              <div className="pt-3">
                <MoreLink onClick={() => navigate('/he-thong/nguoi-dung')}>
                  {t('dashboard.quanLy')}
                </MoreLink>
              </div>
            </div>
          </Tile>

          {/* --- Hàng 4: kho tri thức (8) + phê duyệt (4) --- */}
          <Tile className="col-span-12 lg:col-span-8">
            <TileHeader
              icon={<BookOpen size={16} className="text-content-muted" />}
              title={t('dashboard.khoTriThuc')}
              action={
                <MoreLink onClick={() => navigate('/tri-thuc')}>{t('chung.xemTatCa')}</MoreLink>
              }
            />
            <div className="px-5 py-2">
              {trangThaiO(knowledge) ? (
                trangThaiO(knowledge)
              ) : (knowledge.data ?? []).length === 0 ? (
                <EmptyHint>{t('dashboard.chuaCoBoTriThuc')}</EmptyHint>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {/* Chỉ hiện 6 bộ đầu tiên. */}
                  {(knowledge.data ?? []).slice(0, 6).map((kb) => {
                    const dept = kb.managingDepartmentName ?? t('dashboard.phongBanKhac');
                    const tone = catColor(dept);
                    return (
                      <div key={kb.id} className="flex items-center justify-between gap-3 py-2.5">
                        <BookOpen size={14} className="shrink-0 text-content-muted" />
                        <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                          {kb.name}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-2 py-0.5 text-2xs font-medium',
                            tone.chip,
                            tone.text,
                          )}
                        >
                          {dept}
                        </span>
                        <span className="shrink-0 text-right text-xs font-semibold tabular-nums text-content-secondary">
                          {t('dashboard.soTep', { so: soVN(kb.fileCount, locale) })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tile>

          {/* Ô này không có TileHeader: tiêu đề nằm ngay trong thân ô. */}
          <Tile className="col-span-12 flex flex-col justify-between p-5 lg:col-span-4">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <CheckCircle size={16} className="text-content-secondary" />
                <h2 className="text-sm font-semibold text-content-primary">
                  {t('dashboard.pheDuyetTriThuc')}
                </h2>
              </div>
              <p className="text-sm leading-relaxed text-content-secondary">
                {t('dashboard.pheDuyetMo')}
              </p>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-default pt-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700">
                <Circle size={7} className="fill-success-500 text-success-500" />
                {t('dashboard.sanSang')}
              </span>
              <MoreLink onClick={() => navigate('/tri-thuc/kiem-duyet')}>
                {t('dashboard.mo')}
              </MoreLink>
            </div>
          </Tile>
        </div>
      </div>
    </div>
  );
}
